import { useCallback, useEffect, useState } from "react";
import { Brush, Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_CLEANUP_SETTINGS, type CleanupSettings, type GenericResponse } from "@/lib/types";

export function RetentionPage() {
  const [cfg, setCfg] = useState<CleanupSettings>(DEFAULT_CLEANUP_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<CleanupSettings>("/api/settings/cleanup");
      setCfg(data);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/settings/cleanup", cfg);
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("Automatische Log-Bereinigung")}
        description={t("Alte Log-Einträge werden täglich zur konfigurierten Uhrzeit gelöscht.")}
        actions={
          <Brush className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        }
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("Automatische Bereinigung aktivieren")}</span>
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Aufbewahrung (Tage)")}
                </span>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={cfg.retention_days}
                  onChange={(e) =>
                    setCfg({ ...cfg, retention_days: clamp(parseInt(e.target.value, 10) || 1, 1, 3650) })
                  }
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm tabular-nums outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <span className="text-[11px] text-muted-foreground">
                  {cfg.retention_days === 1
                    ? t("Logs älter als 1 Tag werden gelöscht.")
                    : t("Logs älter als {n} Tage werden gelöscht.", { n: cfg.retention_days })}
                </span>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Ausführungszeit (Stunde, 0–23)")}
                </span>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={cfg.cleanup_hour}
                  onChange={(e) =>
                    setCfg({ ...cfg, cleanup_hour: clamp(parseInt(e.target.value, 10) || 0, 0, 23) })
                  }
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm tabular-nums outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <span className="text-[11px] text-muted-foreground">
                  {t("Läuft täglich um {hh}:00 UTC.", { hh: cfg.cleanup_hour.toString().padStart(2, "0") })}
                </span>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
